import { OrderStatus } from '@features/siteStatusV2/constants';
import {
  FtthInterventionState,
  FtthTerminationType,
  ServiceStatus
} from '@features/siteStatusV2/constants/order';
import { FtthOrder } from '@features/siteStatusV2/types';
import { ThemeVariants } from '@theme';
import { getI18n } from 'react-i18next';

import { TranslationNamespaces } from '../../../../../locales/constants/translationNamespaces.enum';
import { ConfigOrderDetails } from '../../order-details';
import { Reports } from '../../reports';
import { MapStateToKey } from '../maps';

const createFtthDisplayConfig = (order: FtthOrder): ConfigOrderDetails => {
  const { t } = getI18n();

  const traduction = (s1: string, s2?: string) => {
    return s2
      ? t(s1, s2, { ns: TranslationNamespaces.SITE_STATUS })
      : t(s1, { ns: TranslationNamespaces.SITE_STATUS });
  };

  const statusBadgeValue = (): { color: ThemeVariants; text: string } => {
    switch (order.status) {
      case OrderStatus.ACTIVE:
        return { color: 'green', text: traduction('orderDetails.badges.status.active') };
      case OrderStatus.TERMINATED:
        return { color: 'red', text: traduction('orderDetails.badges.status.terminated') };
      case OrderStatus.IN_PROGRESS:
        return { color: 'blue', text: traduction('orderDetails.badges.status.inProgress') };
      case OrderStatus.DELETED:
        return { color: 'red', text: traduction('orderDetails.badges.status.deleted') };
      case OrderStatus.FINISHED:
        return { color: 'orange', text: traduction('orderDetails.badges.status.finished') };
      case OrderStatus.CANCELLED:
        return { color: 'red', text: traduction('orderDetails.badges.status.canceled') };
      default:
        return { text: order.rawStatus ?? 'N/A', color: 'grey' };
    }
  };

  const serviceStatusBadgeValue = (): { color: ThemeVariants; text: string } => {
    switch (order.serviceStatus) {
      case ServiceStatus.ACTIVE:
        return {
          color: 'green',
          text: traduction('orderDetails.generalInformations.serviceStatus.active')
        };
      case ServiceStatus.CANCELED:
        return {
          color: 'red',
          text: traduction('orderDetails.generalInformations.serviceStatus.canceled')
        };
      case ServiceStatus.TERMINATED:
        return {
          color: 'red',
          text: traduction('orderDetails.generalInformations.serviceStatus.terminated')
        };
      default:
        return { text: '-', color: 'grey' };
    }
  };

  const terminationTypeBadgeValue = (): string => {
    switch (order.termination.type) {
      case FtthTerminationType.TERMINATED:
        return traduction('orderDetails.badges.cancelledState.termination');
      case FtthTerminationType.CANCELLED:
        return traduction('orderDetails.badges.cancelledState.cancelation');
      default:
        return order.termination.rawType ?? 'N/A';
    }
  };

  const interventionStateBadgeValue = (): { color: ThemeVariants; text: string } => {
    switch (order.intervention.interventionState) {
      case FtthInterventionState.KO:
        return { color: 'red', text: traduction('orderDetails.badges.interventionState.ko') };
      case FtthInterventionState.OK:
        return { color: 'green', text: traduction('orderDetails.badges.interventionState.ok') };
      case FtthInterventionState.WAITING_REPORT:
        return {
          color: 'blue',
          text: traduction('orderDetails.badges.interventionState.waiting')
        };
      case FtthInterventionState.TO_SEND:
        return {
          color: 'blue',
          text: traduction('orderDetails.badges.interventionState.toSend')
        };
      case FtthInterventionState.SERVICE_PENDING:
        return {
          color: 'blue',
          text: traduction('orderDetails.badges.interventionState.servicePending')
        };
      default:
        return { text: '-', color: 'grey' };
    }
  };

  return {
    header: {
      name: order.referenceServiceTaken,
      headerBadges: [
        {
          color: 'grey',
          label: `${traduction('OrderList.orderDate', `Order Date`)} : ${order.createdDate}`,
          icon: { iconName: 'solid-clock' }
        },
        { color: 'grey', label: order.commercialOperator ?? '' },
        { color: 'blue', label: order.infrastructureOperator ?? '' }
      ]
    },
    sections: [
      {
        title: traduction('orderDetails.cancelled.title'),
        isVisible: order.termination.type !== FtthTerminationType.NO_TYPE,
        color: 'red',
        fields: [
          {
            title: traduction('orderDetails.cancelled.type'),
            icon: 'server',
            value: {
              label: terminationTypeBadgeValue(),
              badge: { color: 'red', full: true, icon: { iconName: 'union' } }
            }
          },
          {
            title: traduction('orderDetails.cancelled.date'),
            icon: 'solid-clock',
            value: { label: order.termination.date ?? '' }
          }
        ]
      },
      {
        title: traduction('orderDetails.generalInformations.title'),
        isVisible: true,
        shouldExport: true,
        fields: [
          {
            title: traduction('orderDetails.generalInformations.referenceServiceTaken'),
            icon: 'code-slash',
            value: { label: order.referenceServiceTaken ?? '' }
          },
          {
            title: traduction('orderDetails.generalInformations.outletReference'),
            icon: 'cube-transparent',
            value: { label: order.outletReference ?? '' }
          },
          {
            title: traduction('orderDetails.generalInformations.status'),
            icon: 'rss',
            value: {
              label: statusBadgeValue().text,
              badge: { color: statusBadgeValue().color, full: false }
            }
          },
          {
            title: traduction('orderDetails.generalInformations.state.title'),
            icon: 'light-bulb',
            value: { label: traduction(MapStateToKey[order.state]) ?? '' }
          },
          {
            title: traduction('orderDetails.generalInformations.connectionMode'),
            icon: 'plug-fill',
            value: { label: order.connectionMode ?? '', badge: { color: 'grey', full: true } }
          },
          {
            title: traduction('orderDetails.generalInformations.serviceStatus.title'),
            icon: 'rss',
            value: {
              label: serviceStatusBadgeValue().text ?? '',
              badge: { color: serviceStatusBadgeValue().color, full: true }
            }
          }
        ]
      },
      {
        isVisible: false,
        shouldExport: true,
        fields: [
          {
            title: traduction('OrderList.orderDate'),
            icon: 'server',
            value: { label: order.createdDate ?? '' }
          },
          {
            title: 'OC',
            icon: 'rss',
            value: { label: order.commercialOperator ?? '' }
          },
          {
            title: 'OI',
            icon: 'rss',
            value: { label: order.infrastructureOperator ?? '' }
          },
          {
            title: traduction('orderDetails.cancelled.type'),
            icon: 'solid-clock',
            value: { label: order.termination.rawType ?? '' }
          },
          {
            title: traduction('orderDetails.cancelled.date'),
            icon: 'solid-clock',
            value: { label: order.termination.date ?? '' }
          }
        ]
      },
      {
        title: traduction('orderDetails.intervention.title'),
        isVisible: true,
        shouldExport: true,
        fields: [
          {
            title: traduction('orderDetails.intervention.interventionDate'),
            icon: 'solid-clock',
            value: { label: order.intervention.interventionDate ?? '' }
          },
          {
            title: traduction('orderDetails.intervention.interventionState'),
            icon: 'solid-bookmark',
            value: {
              label: interventionStateBadgeValue().text,
              badge: { color: interventionStateBadgeValue().color, full: true }
            }
          },
          {
            title: traduction('orderDetails.intervention.reportDate'),
            icon: 'solid-clock',
            value: {
              label: order.intervention.reportDate ?? ''
            }
          },
          {
            title: traduction('orderDetails.intervention.commissioningReportDate'),
            icon: 'solid-clock',
            value: { label: order.intervention.commissioningReportDate ?? '' }
          }
        ]
      },
      {
        custom: <Reports refPrestationPrise={order.referenceServiceTaken ?? ''} />,
        isVisible: true,
        shouldExport: false
      }
    ]
  };
};

export default createFtthDisplayConfig;
